<template>
  <div>
    <ImageOverlay
      v-if="
        (subscriptionStatus === SUBSCRIBED_CUSTOMER_STATUS ||
          subscriptionStatus === TRIAL_CUSTOMER_STATUS) &&
        ($slots.title || $slots.content || $slots.button)
      "
      :class="$style.wrapper"
      :src="imageUrl"
      :focus="focus"
    >
      <template #content>
        <div v-if="!isMobile">
          <div :class="[$style.contentWrapper, $style[fadeAppearance]]">
            <div :class="$style.content">
              <Typography
                :variant="TYPOGRAPHY_TYPES.bodyRegular"
                :class="$style.date"
              >
                <slot name="date" :class="$style.date" />
              </Typography>

              <Typography :variant="TYPOGRAPHY_TYPES.h2" :class="$style.title">
                <slot name="title" />
              </Typography>
            </div>

            <div :class="$style.articlesWrapper">
              <div :class="$style.images">
                <PromotionProduct
                  :promotion-image-url="promotionsImagesUrls[0]"
                  :class="$style.promotionProduct"
                  :promotion-text="packText"
                  :promotion-bold="packBold"
                />

                <Typography
                  :variant="TYPOGRAPHY_TYPES.handwritten"
                  :class="$style.equal"
                >
                  =
                </Typography>

                <PromotionProduct
                  :promotion-image-url="promotionsImagesUrls[1]"
                  :class="$style.promotionProduct"
                  :promotion-text="offerText"
                  :promotion-bold="offerBold"
                />
              </div>

              <div :class="$style.button">
                <slot name="button" />
              </div>
            </div>
          </div>
        </div>

        <div v-else :class="[$style.contentWrapper, $style[fadeAppearance]]">
          <div :class="$style.content">
            <Typography :variant="TYPOGRAPHY_TYPES.h2" :class="$style.title">
              <slot name="title" />
            </Typography>

            <Typography
              :variant="TYPOGRAPHY_TYPES.bodyRegular"
              :class="$style.date"
            >
              <slot name="date" :class="$style.date" />
            </Typography>

            <div :class="$style.images">
              <PromotionProduct
                :promotion-image-url="promotionsImagesUrls[0]"
                :class="$style.promotionProduct"
                :promotion-text="packText"
                :promotion-bold="packBold"
              />

              <Typography
                :variant="TYPOGRAPHY_TYPES.handwritten"
                :class="$style.equal"
              >
                =
              </Typography>

              <PromotionProduct
                :promotion-image-url="promotionsImagesUrls[1]"
                :class="$style.promotionProduct"
                :promotion-text="offerText"
                :promotion-bold="offerBold"
              />
            </div>

            <div :class="$style.button">
              <slot name="button" />
            </div>
          </div>
        </div>
      </template>
    </ImageOverlay>

    <Banner
      v-else-if="subscriptionStatus === UNPAID_CUSTOMER_STATUS"
      image-url="https://japhy-assets.twic.pics/download?id=1_SZ3Luf6dVM0kaw5sJAk5in5FUiOQVm_"
      fade-appearance="none"
    >
      <template #title>{{ $i18n('Abonnement bloqué') }}</template>

      <template #content>
        {{
          $i18n(
            'Mettez à jour vos informations de paiement pour réactiver votre abonnement Japhy',
          )
        }}
      </template>

      <template #button>
        <Button @click="$store.dispatch('modal/open', 'creditCardModal')">
          {{ $i18n('Mettre à jour') }}
        </Button>
      </template>
    </Banner>

    <Banner
      v-else-if="subscriptionStatus === UNSUBSCRIBED_CUSTOMER_STATUS"
      image-url="https://japhy-assets.twic.pics/download?id=1_SZ3Luf6dVM0kaw5sJAk5in5FUiOQVm_"
      fade-appearance="none"
    >
      <template #title>{{ $i18n('Abonnement suspendu') }}</template>

      <template #content>
        {{
          $i18n(
            'Cliquez sur se réabonner pour recevoir de nouvelles livraisons',
          )
        }}
      </template>

      <template #button>
        <Button @click="resubscribe">
          {{ $i18n('Je me réabonne') }}
        </Button>
      </template>
    </Banner>

    <CardConfirmationModal />
  </div>
</template>

<script>
import { SUCCESS } from '@front/common/constants'
import { useConfirmationModal, useMobile } from '@front/common/hooks'
import { isMobile } from '@front/common/mixins'
import { Banner, ImageOverlay, Typography, TYPOGRAPHY_TYPES } from '@front/ui'
import useAlert from '@front/ui/Alert/useAlert'
import { computed, defineComponent } from '@vue/composition-api'

import useCustomer from '@member-area/core/resources/Customer/Customer.hook'
import {
  SUBSCRIBED_CUSTOMER_STATUS,
  TRIAL_CUSTOMER_STATUS,
  UNPAID_CUSTOMER_STATUS,
  UNSUBSCRIBED_CUSTOMER_STATUS,
} from '@member-area/core/resources/Customer/constants'
import useNextOrders from '@member-area/core/resources/NextOrder/NextOrder.hook'
import usePet from '@member-area/core/resources/Pet/Pet.hook'
import useSubscription from '@member-area/core/resources/Subscription/Subscription.hook'
import CardConfirmationModal from '@member-area/pages/Home/components/Unsubscribed/components/CardConfirmationModal.vue'

import PromotionProduct from './components/PromotionProduct.vue'

export default defineComponent({
  components: {
    Banner,
    CardConfirmationModal,
    ImageOverlay,
    Typography,
    PromotionProduct,
  },
  mixins: [isMobile],
  props: {
    imageUrl: {
      type: String,
      default: '',
    },
    fadeAppearance: {
      type: String,
      default: 'false',
    },
    promotionsImagesUrls: {
      type: Array,
      default: () => [],
    },
    packText: {
      type: String,
      default: 'UN PACK POISSON GOURMET {}',
    },
    offerText: {
      type: String,
      default: 'UN PACK OFFRE',
    },
    packBold: {
      type: String,
      default: 'AJOUTÉ',
    },
    offerBold: {
      type: String,
      default: 'OFFERT',
    },
    focus: {
      type: String,
      default: 'auto',
    },
  },
  setup() {
    const { customer } = useCustomer()
    const { confirm } = useConfirmationModal()
    const { fetchCustomer, resubscribeCustomer } = useCustomer()
    const { fetchSubscription } = useSubscription()
    const { fetchPets } = usePet()
    const { fetchNextOrders } = useNextOrders()
    const { triggerAlert } = useAlert()

    const subscriptionStatus = computed(() => {
      return customer.value.subscriptionStatus
    })

    const isMobile = useMobile()

    async function resubscribe() {
      await confirm()
      await resubscribeCustomer()
      await Promise.all([fetchSubscription(), fetchPets(), fetchNextOrders()])
      await fetchCustomer()

      triggerAlert({
        status: SUCCESS,
        message:
          'Votre réabonnement a été effectué avec succès. Bienvenue à nouveau !',
      })
    }

    return {
      subscriptionStatus,
      SUBSCRIBED_CUSTOMER_STATUS,
      TRIAL_CUSTOMER_STATUS,
      UNPAID_CUSTOMER_STATUS,
      UNSUBSCRIBED_CUSTOMER_STATUS,
      TYPOGRAPHY_TYPES,
      isMobile,
      resubscribe,
    }
  },
})
</script>

<style lang="scss" module>
.wrapper {
  height: 320px;
  overflow: hidden;
  border-radius: 0px;
  margin: 0 -10px;

  @media #{map-get($display-breakpoints, 'lg-and-up')} {
    border-radius: 16px;
  }
}

.contentWrapper {
  height: 100%;
  width: 100%;
  padding: $ds-spacing-sm;
  display: flex;
  flex-direction: row;

  &.top-whitefade {
    background: linear-gradient(
      0deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.5) 40.25%,
      rgba(255, 255, 255, 0.6) 74.59%
    );
  }

  &.bottom-whitefade {
    background: linear-gradient(
      0deg,
      rgba(255, 255, 255, 0.6) 0%,
      rgba(255, 255, 255, 0.5) 30.25%,
      rgba(255, 255, 255, 0) 50.59%
    );
  }

  &.left-whitefade {
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.6) 0%,
      rgba(255, 255, 255, 0.5) 25.5%,
      rgba(255, 255, 255, 0) 74.59%
    );
  }

  &.left-darkfade {
    background: linear-gradient(
      90deg,
      rgba(0, 0, 0, 0.3) 0%,
      rgba(0, 0, 0, 0.2) 25.5%,
      rgba(0, 0, 0, 0) 74.59%
    );

    .title,
    .date,
    .contentSlot,
    .cgv {
      color: white;
    }
  }

  &.top-darkfade {
    background: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.2) 40.25%,
      rgba(0, 0, 0, 0.3) 74.59%
    );

    .title,
    .date,
    .contentSlot,
    .cgv {
      color: white;
    }
  }

  @media #{map-get($display-breakpoints, 'lg-and-up')} {
    padding: 0px $ds-spacing-xl;
  }
}

.content {
  padding-top: $ds-spacing-xl;
  flex-shrink: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1;
  width: 100%;
  flex: 1;

  @media #{map-get($display-breakpoints, 'lg-and-up')} {
    justify-content: center;
    flex: 1;
  }
}

.articlesWrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: $ds-spacing-xl;
  padding-right: $ds-spacing-lg;
  flex: 2;
  margin-left: -$ds-spacing-xl;
}

.images {
  display: flex;
  flex-direction: row;
  align-items: center;
  align-self: center;
  padding: $ds-spacing-2xs 0;
}

.promotionProduct {
  @media #{map-get($display-breakpoints, 'lg-and-up')} {
    padding-left: $ds-spacing-sm;
    padding-right: $ds-spacing-sm;
  }
}

.contentSlot {
  text-align: center;
  margin-bottom: $ds-spacing-xs;
  max-width: 90%;
  display: flex;
  flex-direction: row;
  gap: $ds-spacing-md;
  width: 100%;

  :global(img) {
    width: 100%;
    height: auto;
    border-radius: 8px;
  }
}

.title {
  text-align: center;
  color: white;
  width: 100%;
  margin-bottom: $ds-spacing-xs;
  line-height: 1;

  @media #{map-get($display-breakpoints, 'lg-and-up')} {
    text-align: left;
  }
}

.date {
  text-align: center;
  color: white;
  width: 100%;
  margin-bottom: $ds-spacing-xs;
  line-height: 1;

  @media #{map-get($display-breakpoints, 'lg-and-up')} {
    padding-top: $ds-spacing-xl;
    text-align: left;
  }
}

.button {
  align-self: center;
  padding-bottom: 32px;

  @media #{map-get($display-breakpoints, 'lg-and-up')} {
    width: auto;
  }
}

.equal {
  color: white;

  @media #{map-get($display-breakpoints, 'lg-and-up')} {
    align-self: center;
  }
}
</style>
